import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, FileSearch, AlertTriangle, FileText, CheckCircle2 } from 'lucide-react';
import Navbar from './Navbar';

const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_tender-ai-review/artifacts/gbh4pzs3_ChatGPT%20Image%203%20%D0%BC%D0%B0%D1%80.%202026%20%D0%B3.%2C%2003_13_11.png';

export default function Hero() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="pt-4 sm:pt-5 hero-bottom-spacing"
    >
      <div className="section-container">
        {/* === Contained rounded hero block === */}
        <div className="hero-main-container relative overflow-hidden rounded-[28px]">
          {/* Background image — positioned at bottom so mountains show at base */}
          <div
            className="absolute inset-0 bg-cover bg-bottom bg-no-repeat blur-[2px] scale-[1.02]"
            style={{ backgroundImage: `url(${BG_IMAGE})` }}
          />
          {/* Overlay: solid dark sage — darker for stronger contrast */}
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to bottom, hsla(150,12%,17%,0.92) 0%, hsla(150,12%,17%,0.92) 55%, hsla(150,12%,17%,0.88) 75%, hsla(150,12%,17%,0.82) 90%, hsla(150,12%,17%,0.72) 100%)'
          }} />
          {/* Warm glow bottom-left */}
          <div className="absolute bottom-0 left-0 w-[500px] h-[400px] bg-[radial-gradient(circle_at_0%_100%,_rgba(200,160,80,0.15)_0%,_transparent_60%)]" />

          {/* Content */}
          <div className="relative z-10">
            {/* Navbar inside container */}
            <div className="px-5 sm:px-8 lg:px-10 pt-4">
              <Navbar variant="transparent" />
            </div>

            {/* Hero copy — centered */}
            <div className="text-center px-5 sm:px-8 pt-12 sm:pt-20 lg:pt-28 pb-0">
              <p
                data-animate
                className="text-xs uppercase tracking-[0.22em] text-white/40 mb-5"
              >
                AI-Powered Tender Intelligence
              </p>

              <h1
                data-animate
                style={{ transitionDelay: '80ms' }}
                className="font-display text-3xl sm:text-4xl lg:text-[3.2rem] tracking-[-0.01em] leading-[1] lg:leading-[1.1] text-white max-w-3xl mx-auto mb-5"
              >
                Read Tender Packs Like an Expert. Decide Faster.
              </h1>

              <p
                data-animate
                style={{ transitionDelay: '160ms' }}
                className="text-sm sm:text-base text-white leading-relaxed max-w-xl mx-auto mb-8"
              >
                Upload tender documentation and get eligibility checks, compliance
                mapping, risk flags, and structured draft applications ready the same day.
              </p>

              <div
                data-animate
                style={{ transitionDelay: '240ms' }}
                className="flex items-center justify-center gap-3"
              >
                <Button
                  data-testid="hero-primary-cta-button"
                  onClick={() => scrollTo('#waitlist')}
                  className="bg-white text-[hsl(150,18%,18%)] hover:bg-white/90 h-11 px-7 text-sm font-semibold rounded-full shadow-float"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* ─── Glowing arc + Cards ─── */}
            <div className="relative mt-14 sm:mt-20 pb-0">
              {/* Glowing arc */}
              <div className="hero-glow-arc" />

              {/* Three glassmorphic cards */}
              <div
                data-testid="hero-product-mockup"
                data-animate="scale"
                style={{ transitionDelay: '350ms' }}
                className="relative z-10 flex flex-col sm:flex-row items-end justify-center gap-4 sm:gap-5 px-5 sm:px-8 lg:px-10 -translate-y-[26px] sm:-translate-y-[26px]"
              >
                {/* Card 1: Document Map */}
                <div className="glass-hero-card p-5 w-full sm:w-[280px] lg:w-[300px] float-1">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                      <FileSearch className="w-4 h-4 text-emerald-300" />
                    </div>
                    <span className="text-xs font-semibold text-white/90">Document Map</span>
                    <Badge variant="outline" className="ml-auto text-[9px] border-white/15 text-white/50">
                      14 sections
                    </Badge>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      { name: 'Scope of Work', pct: 100 },
                      { name: 'Eligibility Criteria', pct: 100 },
                      { name: 'Evaluation Method', pct: 85 },
                      { name: 'Insurance Req.', pct: 60 },
                    ].map((s) => (
                      <div key={s.name} className="flex items-center gap-2.5">
                        <span className="text-[10px] text-white/40 w-28 truncate">{s.name}</span>
                        <div className="flex-1 score-bar-hero">
                          <div className="score-bar-hero-fill" style={{ width: `${s.pct}%` }} />
                        </div>
                        <span className="text-[10px] font-mono text-white/30 w-7 text-right">{s.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card 2: Risk Assessment (center, slightly taller) */}
                <div className="glass-hero-card p-5 w-full sm:w-[280px] lg:w-[320px] sm:-translate-y-4 float-2">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-amber-300" />
                    </div>
                    <span className="text-xs font-semibold text-white/90">Risk Assessment</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 rounded-lg bg-red-500/10 border border-red-500/10 px-3 py-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                      <span className="text-[11px] text-red-300/90">Liquidated damages — 5 day threshold</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 border border-amber-500/10 px-3 py-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                      <span className="text-[11px] text-amber-300/90">16-week timeline is aggressive</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/10 px-3 py-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                      <span className="text-[11px] text-emerald-300/90">Pricing model advantage identified</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/8 flex items-center justify-between">
                    <span className="text-[10px] text-white/30">Overall verdict</span>
                    <span className="text-xs font-medium text-amber-300">Bid with conditions</span>
                  </div>
                </div>

                {/* Card 3: Draft Outline */}
                <div className="glass-hero-card p-5 w-full sm:w-[280px] lg:w-[300px] float-3 hidden sm:block">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 rounded-lg bg-sky-500/15 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-sky-300" />
                    </div>
                    <span className="text-xs font-semibold text-white/90">Draft Outline</span>
                    <span className="ml-auto text-[9px] font-mono text-white/25">auto-generated</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: 'Executive Summary', done: true },
                      { label: 'Demonstrated Experience', done: true },
                      { label: 'Key Personnel', done: true },
                      { label: 'Proposed Methodology', done: false },
                      { label: 'Risk Mitigation Plan', done: false },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-2.5">
                        {item.done ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border border-white/15 shrink-0" />
                        )}
                        <span className={`text-[11px] ${item.done ? 'text-white/60' : 'text-white/30'}`}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
